import React, { useMemo, useRef, useState } from 'react';
import { Spin } from 'antd';
import styled from 'styled-components';

import FormButton from '@components/form/form-button';
import ButtonNext from '@domain/button-next';
import ConfirmModal from '@domain/confirm-modal';
import Image from '@domain/image';
import Layout from '@domain/layout';
import { useVismaConfiguration } from '@services/visitor-management';
import { useFormStore } from '@store/use-form-store';

import useInterval from '../../hooks/useInterval';
import MultipleRegistrationVideoBanner from './multiple-registration-video-banner';
import { useIntl } from 'react-intl';

const Container = styled.div`
  width: 100%;

  @media only screen and (max-width: 1200px) {
    width: 100%;
    padding-bottom: 10vh;
  }
`;

const HeaderText = styled.h1`
  font-size: 30px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  padding-bottom: 16px;

  @media only screen and (max-width: 1200px) {
    margin: 16px 20px;
  }
`;

const DescText = styled.h6`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.textSecondary};
  margin: 24px 0;
  white-space: break-spaces;

  & > b {
    color: ${({ theme }) => theme.primary};
  }

  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 3em 0 5em 0;
  justify-content: space-between;
  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: 16px;

  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
    & > input {
      min-width: 20px;
    }
  }
`;

const Radio = styled.input`
  width: 2em;
  height: 2em;
  accent-color: ${({ theme }) => theme.primary};
  margin-right: 1em;
`;

const GrayText = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
`;

const MobileOnlyBanner = styled.div`
  display: none;
  @media only screen and (max-width: 1200px) {
    display: block;
    background: ${({ theme }) => theme.backgroundInput};
    margin: -20px 20px 24px 20px;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.border};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const Video = styled.video`
  width: 100%;
  max-height: 400px;
  border-radius: 16px;
  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
    width: -webkit-fill-available;
  }
`;

interface VideoFeatureProps {
  onElearningComplete: (accepted: boolean) => Promise<void>;
  isLoading: boolean;
  onPreviousPage: () => void;
  children?: React.ReactNode;
}

function VideoFeature({
  onElearningComplete,
  isLoading,
  onPreviousPage,
  children
}: VideoFeatureProps) {
  const intl = useIntl();
  const { configuration } = useVismaConfiguration();
  const elearning = configuration?.elearning;
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const [selected, setSelected] = useState('');
  const [confirmModal, setConfirmModal] = useState(false);
  const [playedSecs, setPlayedSecs] = useState(0);
  const { registrationType, setVideoWatched } = useFormStore();

  const handleClickNext = async () => {
    try {
      if (
        isVideoEnded &&
        selected === 'accepted' &&
        registrationType !== 'multiple'
      ) {
        await onElearningComplete(true);
      }
    } catch {}
  };

  useMemo(() => {
    if (selected === 'declined') {
      setConfirmModal(true);
    }
  }, [selected]);

  useInterval(() => {
    if (videoRef.current && videoRef.current.currentTime > 0) {
      setPlayedSecs(videoRef.current.currentTime);
    }
  }, 300);

  const rightPanel =
    registrationType === 'multiple' ? (
      <MultipleRegistrationVideoBanner />
    ) : (
      <Image
        id={elearning?.featured_image?.id as string}
        alt="video-banner"
        style={{ height: '100%', width: '100%', objectFit: 'contain' }}
      />
    );

  return (
    <Spin spinning={isLoading} size="large">
      <Layout rightPanel={rightPanel}>
        <Container>
          <ConfirmModal
            open={confirmModal}
            closeModal={() => {
              setConfirmModal(false);
              setSelected('');
            }}
            leftButtonTitle={intl.formatMessage({ id: 'CLOSE' })}
            rightButtonTitle={intl.formatMessage({ id: 'FINISH_AND_EXIT' })}
            leftButtonWithArrow={false}
            rightButtonWithArrow={false}
            title={elearning?.decline_popup_title}
            description={elearning?.decline_popup_description}
            onQuit={() => onElearningComplete(false).catch(() => {})}
          />
          <HeaderText>{elearning?.title}</HeaderText>
          <DescText
            dangerouslySetInnerHTML={{
              __html: elearning?.description ?? ''
            }}
          />
          <Video
            src={elearning?.elearning_video_url}
            controls
            autoPlay={false}
            ref={videoRef}
            onPlaying={() => {}}
            onSeeking={() => {
              if (!videoRef.current) return;
              const delta = videoRef.current.currentTime - playedSecs;
              if (Math.abs(delta) > 0.01) {
                videoRef.current.currentTime = playedSecs;
              }
            }}
            onEnded={() => {
              setVideoWatched();
              setIsVideoEnded(true);
              setPlayedSecs(0);
            }}
            disablePictureInPicture
            disableRemotePlayback
            controlsList={'nodownload noplaybackrate noseek'}
          />

          {isVideoEnded && registrationType !== 'multiple' && (
            <>
              <RadioGroup>
                <Radio
                  type="radio"
                  checked={selected === 'accepted'}
                  onChange={(e) =>
                    e.target.checked ? setSelected('accepted') : null
                  }
                />
                <GrayText onClick={() => setSelected('accepted')}>
                  {elearning?.elearning_accept}
                </GrayText>
              </RadioGroup>

              <RadioGroup>
                <Radio
                  type="radio"
                  checked={selected === 'declined'}
                  onChange={(e) =>
                    e.target.checked ? setSelected('declined') : null
                  }
                />
                <GrayText onClick={() => setSelected('declined')}>
                  {elearning?.elearning_decline}
                </GrayText>
              </RadioGroup>
            </>
          )}
          {registrationType !== 'multiple' && (
            <ButtonContainer>
              <FormButton
                style={{ marginRight: '1em' }}
                onClick={onPreviousPage}
                withLeftArrow
              >
                {configuration?.generalInformation?.button_cancel}
              </FormButton>
              <ButtonNext
                onClick={handleClickNext}
                disabled={selected !== 'accepted'}
                isLoading={isLoading}
                withRightArrow
              />
            </ButtonContainer>
          )}
          {children}
        </Container>
        {registrationType === 'multiple' && (
          <MobileOnlyBanner>
            <MultipleRegistrationVideoBanner />
          </MobileOnlyBanner>
        )}
      </Layout>
    </Spin>
  );
}

export default VideoFeature;
